import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Palette,
  Image,
  ArrowRight,
  Upload,
  Trash,
  SpinnerGap,
  MagicWand,
  Camera
} from '@phosphor-icons/react';
import api from '../services/api';
import { toast } from 'sonner';
import useEventTypeConfig from '../hooks/useEventTypeConfig';
import AIPremiumButton, {
  AI_USAGE_IMAGE_READING,
  AI_USAGE_LUNA_TEXT,
} from './AIPremiumGate';
import BackgroundImageOptions from './IncludedThemeOptions';
import ThemeOptionCard from './ThemeOptionCard';
import { DEFAULT_THEME_OPTIONS } from '../config/defaultThemeOptions';
import InfoTip from './InfoTip';

const getApiErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallbackMessage;
};

export default function ThemeSelector({ eventId, currentTheme, onThemeUpdate, eventData }) {
  const eventTypeConfig = useEventTypeConfig(eventData);
  const [prompt, setPrompt] = useState(currentTheme?.prompt || '');
  const [suggestions, setSuggestions] = useState(currentTheme?.suggestions || []);
  const [selectedThemeId, setSelectedThemeId] = useState(currentTheme?.selected_theme_id || null);
  const [generatedBgOptions, setGeneratedBgOptions] = useState(
    currentTheme?.generated_image_urls || (currentTheme?.generated_image_url ? [currentTheme.generated_image_url] : [])
  );
  const [selectedBgImage, setSelectedBgImage] = useState(
    currentTheme?.selected_background_url
      || currentTheme?.custom_image_url
      || currentTheme?.generated_image_url
      || null
  );
  const [uploadedBgImage, setUploadedBgImage] = useState(currentTheme?.custom_image_url || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [isGeneratingBgImage, setIsGeneratingBgImage] = useState(false);
  const [generationMode, setGenerationMode] = useState('prompt'); // 'prompt' or 'image'
  const [sourceImageUrl, setSourceImageUrl] = useState(currentTheme?.source_image_url || null);
  const fileInputRef = useRef(null);
  const themeImageInputRef = useRef(null);
  const bgImageInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const defaultThemeIds = new Set(DEFAULT_THEME_OPTIONS.map((theme) => theme.id));
  const includedThemeOptions = DEFAULT_THEME_OPTIONS.map((theme) => {
    if (eventTypeConfig.isWedding) return theme;
    if (theme.id === 'default-royal-romance') {
      return {
        ...theme,
        name: eventTypeConfig.isBabyShower ? 'Royal Welcome' : 'Royal Editorial',
        mood: eventTypeConfig.isBabyShower ? 'joyful' : 'premium',
      };
    }
    if (theme.id === 'default-garden-bliss') {
      return {
        ...theme,
        mood: eventTypeConfig.isBabyShower ? 'joyful' : 'fresh',
      };
    }
    return theme;
  });
  const customSuggestions = suggestions.filter((theme) => !defaultThemeIds.has(theme.id));

  useEffect(() => {
    setUploadedBgImage(currentTheme?.custom_image_url || null);
  }, [eventId, currentTheme?.custom_image_url]);

  const generateBackgroundForCurrentTheme = async (themePrompt = prompt, nextTheme = null) => {
    setIsGeneratingBgImage(true);
    try {
      const formData = new FormData();
      if (themePrompt?.trim()) {
        formData.append('prompt', themePrompt.trim());
      }

      const response = await api.post(`/events/${eventId}/generate-theme-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const urls = response.data.urls || (response.data.url ? [response.data.url] : []);
      setGeneratedBgOptions(urls);
      setSelectedBgImage(response.data.url || null);

      if (onThemeUpdate) {
        onThemeUpdate({
          ...currentTheme,
          ...nextTheme,
          generated_image_urls: urls,
          generated_image_url: response.data.url || null,
          selected_background_url: response.data.url || null
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error generating background image:', error);
      throw error;
    } finally {
      setIsGeneratingBgImage(false);
    }
  };

  const applyThemeSelection = async (themeId, { autoGenerateBackground = false, themePrompt = prompt } = {}) => {
    const response = await api.put(`/events/${eventId}/select-theme?theme_id=${themeId}`);
    setSelectedThemeId(themeId);

    if (onThemeUpdate) {
      onThemeUpdate(response.data.theme);
    }

    if (autoGenerateBackground) {
      await generateBackgroundForCurrentTheme(themePrompt, {
        selected_theme: response.data.theme,
        selected_theme_id: themeId
      });
    }

    return response.data.theme;
  };

  const handleGenerateThemes = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a theme description');
      return;
    }

    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);

      const response = await api.post(`/events/${eventId}/generate-themes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const generatedSuggestions = response.data.suggestions || [];
      setSuggestions(generatedSuggestions);
      setSelectedThemeId(null);
      setSourceImageUrl(null);

      if (generatedSuggestions.length > 0) {
        try {
          await applyThemeSelection(generatedSuggestions[0].id, {
            autoGenerateBackground: true,
            themePrompt: prompt
          });
          toast.success('Theme suggestions and background generated!');
        } catch (backgroundError) {
          console.error('Error auto-generating background:', backgroundError);
          toast.success('Theme suggestions generated!');
          toast.error('Background generation failed. You can still generate it manually.');
        }
      } else {
        toast.success('Theme suggestions generated!');
      }
    } catch (error) {
      console.error('Error generating themes:', error);
      toast.error('Failed to generate themes. Using default suggestions.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnalyzeImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsAnalyzingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/events/${eventId}/analyze-theme-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const generatedSuggestions = response.data.suggestions || [];
      setSuggestions(generatedSuggestions);
      setSourceImageUrl(response.data.source_image_url);
      setSelectedThemeId(null);
      setPrompt('');

      if (generatedSuggestions.length > 0) {
        try {
          await applyThemeSelection(generatedSuggestions[0].id, {
            autoGenerateBackground: true,
            themePrompt: response.data.prompt || ''
          });
          toast.success('Theme suggestions and background generated from your image!');
        } catch (backgroundError) {
          console.error('Error auto-generating background from image:', backgroundError);
          toast.success(response.data.message);
          toast.error('Background generation failed. You can still generate it manually.');
        }
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      toast.error('Failed to analyze image');
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  const handleSelectTheme = async (themeId) => {
    try {
      await applyThemeSelection(themeId);
      toast.success('Theme applied!');
    } catch (error) {
      console.error('Error selecting theme:', error);
      toast.error(getApiErrorMessage(error, 'Failed to apply theme'));
    }
  };

  const handleGenerateBgImage = async () => {
    if (!selectedThemeId) {
      toast.error('Please select a theme first');
      return;
    }

    try {
      const response = await generateBackgroundForCurrentTheme();
      toast.success(response.message || 'Theme background generated!');
    } catch (error) {
      toast.error('Failed to generate theme background');
    }
  };

  const handleSelectBgImage = async (url) => {
    try {
      await api.put(`/events/${eventId}/select-theme-image?image_url=${encodeURIComponent(url)}`);
      setSelectedBgImage(url);
      toast.success('Theme background selected!');
      if (onThemeUpdate) {
        const isUploadedBackground = url === uploadedBgImage;
        onThemeUpdate({
          ...currentTheme,
          custom_image_url: uploadedBgImage,
          generated_image_urls: generatedBgOptions,
          generated_image_url: isUploadedBackground
            ? currentTheme?.generated_image_url
            : url,
          selected_background_url: url
        });
      }
    } catch (error) {
      console.error('Error selecting background image:', error);
      toast.error(getApiErrorMessage(error, 'Failed to select theme background'));
    }
  };

  const handleUploadBgImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/events/${eventId}/theme-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSelectedBgImage(response.data.url);
      setUploadedBgImage(response.data.url);
      toast.success('Theme background uploaded!');
      if (onThemeUpdate) {
        onThemeUpdate({
          ...currentTheme,
          custom_image_url: response.data.url,
          selected_background_url: response.data.url
        });
      }
    } catch (error) {
      console.error('Error uploading background image:', error);
      toast.error('Failed to upload background image');
    }
  };

  const handleRemoveUploadedBackground = async () => {
    try {
      const response = await api.delete(`/events/${eventId}/theme-image`);
      const fallbackBackground = response.data.selected_background_url || null;
      setSelectedBgImage(fallbackBackground);
      setUploadedBgImage(null);
      toast.success('Uploaded background removed');
      if (onThemeUpdate) {
        onThemeUpdate({
          ...currentTheme,
          custom_image_url: null,
          selected_background_url: fallbackBackground
        });
      }
    } catch (error) {
      console.error('Error removing uploaded background image:', error);
      toast.error(getApiErrorMessage(error, 'Failed to remove uploaded background'));
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/events/${eventId}/couple-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${eventTypeConfig.labels.logoLabel} uploaded!`);
      if (onThemeUpdate) {
        onThemeUpdate({ ...currentTheme, couple_logo_url: response.data.url });
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(`Failed to upload ${eventTypeConfig.labels.logoLabel.toLowerCase()}`);
    }
  };

  const handleClearTheme = async () => {
    try {
      await api.delete(`/events/${eventId}/theme`);
      setSuggestions([]);
      setSelectedThemeId(null);
      setSourceImageUrl(null);
      setPrompt('');
      setGeneratedBgOptions([]);
      setSelectedBgImage(null);
      toast.success('Theme cleared');
      if (onThemeUpdate) {
        onThemeUpdate(null);
      }
    } catch (error) {
      console.error('Error clearing theme:', error);
      toast.error('Failed to clear theme');
    }
  };

  return (
    <div className="space-y-6">
      {/* Event Logo Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Camera className="w-5 h-5" />
            {eventTypeConfig.labels.logoLabel}
            <InfoTip content={eventTypeConfig.labels.logoDescription} label={`About ${eventTypeConfig.labels.logoLabel}`} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {currentTheme?.couple_logo_url && (
              <img 
                src={currentTheme.couple_logo_url}
                alt={eventTypeConfig.labels.logoLabel}
                className="w-16 h-16 rounded-lg object-cover border"
              />
            )}
            <div className="flex-1">
              <Button 
                variant="outline" 
                onClick={() => logoInputRef.current?.click()}
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {currentTheme?.couple_logo_url ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleUploadLogo}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme Generation */}
      <Card>
        <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Palette className="w-5 h-5" />
            {eventTypeConfig.labels.themeTitle}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div data-testid="included-theme-options">
            <div className="mb-3 flex items-center gap-1">
              <h3 className="font-medium">Included themes</h3>
              <InfoTip content="Choose a ready-made color, typography, and decoration style without a subscription." label="About included themes" />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3">
              {includedThemeOptions.map((theme) => (
                <ThemeOptionCard
                  key={theme.id}
                  theme={theme}
                  isSelected={selectedThemeId === theme.id}
                  onSelect={handleSelectTheme}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1 border-t pt-5">
            <h3 className="font-medium">Create a custom theme</h3>
            <InfoTip content="Describe a look or upload inspiration to create matching options." label="About custom themes" />
          </div>

          {/* Toggle between generate modes */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={generationMode === 'prompt' ? "default" : "outline"}
              onClick={() => setGenerationMode('prompt')}
              className={generationMode === 'prompt' ? "bg-[var(--theme-primary,#5D7061)]" : ""}
              data-testid="prompt-mode-btn"
            >
              <MagicWand className="w-4 h-4 mr-2" />
              Describe Theme
            </Button>
            <Button
              variant={generationMode === 'image' ? "default" : "outline"}
              onClick={() => setGenerationMode('image')}
              className={generationMode === 'image' ? "bg-[var(--theme-primary,#5D7061)]" : ""}
              data-testid="image-mode-btn"
            >
              <Image className="w-4 h-4 mr-2" />
              Upload Inspiration Image
            </Button>
          </div>

          {generationMode === 'prompt' ? (
            <>
              {/* Theme Description Input */}
              <div>
                <Label htmlFor="theme-prompt">{eventTypeConfig.labels.themePromptLabel}</Label>
                <Textarea
                  id="theme-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={eventTypeConfig.labels.themePromptPlaceholder}
                  rows={3}
                  className="mt-1"
                  data-testid="theme-prompt-input"
                />
              </div>

              <AIPremiumButton
                featureName="AI theme suggestions"
                usageCategory={AI_USAGE_LUNA_TEXT}
                className="w-full bg-[var(--theme-primary,#5D7061)] hover:opacity-90"
                onClick={handleGenerateThemes}
                data-testid="generate-themes-btn"
              >
                {eventTypeConfig.labels.themePromptButton}
              </AIPremiumButton>
            </>
          ) : (
            /* Image Upload Mode - Analyze image to generate themes */
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              {sourceImageUrl ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-2">Source Image</p>
                  <img 
                    src={sourceImageUrl}
                    alt="Source"
                    className="max-w-full h-40 mx-auto rounded-lg object-cover"
                  />
                  <AIPremiumButton
                    featureName="AI inspiration image analysis"
                    usageCategory={AI_USAGE_IMAGE_READING}
                    variant="outline"
                    onClick={() => themeImageInputRef.current?.click()}
                  >
                    Analyze Different Image
                  </AIPremiumButton>
                </div>
              ) : (
                <>
                  <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">
                    Upload an image and we'll extract colors, patterns, and mood to suggest matching themes
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {eventTypeConfig.labels.imageUploadHint}
                  </p>
                  <AIPremiumButton
                    featureName="AI inspiration image analysis"
                    usageCategory={AI_USAGE_IMAGE_READING}
                    data-testid="upload-theme-image-btn"
                    onClick={() => themeImageInputRef.current?.click()}
                  >
                    Analyze Inspiration Image
                  </AIPremiumButton>
                </>
              )}
              <input
                ref={themeImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAnalyzeImage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Theme Suggestions */}
      {customSuggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Theme Suggestions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
              {customSuggestions.map((theme) => (
                <ThemeOptionCard
                  key={theme.id}
                  theme={theme}
                  isSelected={selectedThemeId === theme.id}
                  onSelect={handleSelectTheme}
                />
              ))}
            </div>

          </CardContent>
        </Card>
      )}

      <BackgroundImageOptions
        selectedBackground={selectedBgImage}
        uploadedBackground={uploadedBgImage}
        currentTheme={currentTheme}
        onSelectBackground={handleSelectBgImage}
        generatedBackgrounds={generatedBgOptions}
        onGenerateBackground={handleGenerateBgImage}
        backgroundInputRef={bgImageInputRef}
        onUploadBackground={handleUploadBgImage}
        onRemoveUploadedBackground={handleRemoveUploadedBackground}
        viewAudienceLabel={eventTypeConfig.labels.themeViewAudience}
      />

      {/* Clear Theme Button */}
      {selectedThemeId && (
        <Button
          variant="ghost"
          onClick={handleClearTheme}
          className="text-destructive hover:text-destructive"
          data-testid="clear-theme-btn"
        >
          <Trash className="w-4 h-4 mr-2" />
          Clear Theme & Start Over
        </Button>
      )}
    </div>
  );
}
